import { addStepToGraph, cloneWorkflowGraph } from '../graph/index.js';
import { evaluateStepMessage } from './evaluateStepMessage.js';
import { validateStep } from './validateStep.js';

export const createWorkflowStep = async ({ data, app, workflow, insertBefore }) => {
  const parentStepId = data?.parentStepId;
  const relation = data?.relation || 'exit';
  const index = data?.index;

  const payload = {
    id: data?.id,
    companyId: data?.companyId,
    workflowId: data?.workflowId,
    appId: data.appId,
    appVersion: data?.appVersion,
    appSlug: app?.slug,
    credentialId: data?.credentialId,
    integrationAppId: data?.integrationAppId,
    actionId: data?.actionId,
    type: data?.type,
    isMessageManual: false,
    isValid: data?.isValid,
    isSkipped: false,
    arguments: data?.arguments || {},
    options: data?.options || {},
    meta: { branches: app?.slug === 'ifElse' ? 2 : 0 },
    createdOn: new Date(),
    updatedOn: new Date(),
    createdBy: data?.createdBy || data?.userId,
    updatedBy: data?.createdBy || data?.userId
  };

  payload.message = await evaluateStepMessage({
    workflow,
    app,
    step: payload
  });

  const validation = await validateStep({ workflow, app, step: payload });
  payload.isValid = validation?.status === 'valid';
  payload.meta.validation = validation;

  // Check BOTH app metadata type AND the explicit data.type passed by the caller
  // This ensures triggers are handled correctly even if app metadata doesn't have type: 'trigger'
  const isTriggerOrInput = app?.type === 'trigger' || app?.type === 'input' || data?.type === 'trigger' || data?.type === 'input';

  if (isTriggerOrInput) {
    return {
      graph: workflow?.graph,
      step: payload
    };
  }

  let graph = cloneWorkflowGraph(workflow?.graph);
  const steps = workflow?.steps || [];
  const actionSteps = steps.filter((s) => !['trigger', 'input'].includes(s.type));
  const isFirstActionStep = actionSteps.length === 0;

  if (insertBefore) {
    if (!graph?.[insertBefore]) {
      throw new Error(`Cannot add step before "${insertBefore}": target step does not exist in the graph.`);
    }

    // Initialize our new step in the graph with proper connections
    graph[data.id] = {
      exit: insertBefore,
      child: null,
      branches: []
    };

    // Find all steps that point to our insertBefore step
    const pointingSteps = Object.entries(graph).filter(([id, node]) => {
      return id !== data.id && (node.exit === insertBefore || node.child === insertBefore || node.branches?.includes(insertBefore));
    });

    // Update all pointing steps to point to our new step instead
    pointingSteps.forEach(([id, node]) => {
      if (node.exit === insertBefore) {
        graph[id].exit = data.id;
      }
      if (node.child === insertBefore) {
        graph[id].child = data.id;
      }
      if (node.branches?.includes(insertBefore)) {
        graph[id].branches = node.branches.map((b) => (b === insertBefore ? data.id : b));
      }
    });

    // Special handling for first action step
    if (isFirstActionStep || insertBefore === actionSteps[0]?.id) {
      // Update any trigger/input steps
      steps
        .filter((s) => ['trigger', 'input'].includes(s.type))
        .forEach((triggerStep) => {
          if (graph[triggerStep.id]?.exit === insertBefore) {
            graph[triggerStep.id].exit = data.id;
          }
        });
    }
  } else {
    // Normal addition logic
    // CRITICAL: Check if parentStepId is a trigger/input - if so, don't pass it as parent because triggers don't belong in the workflow graph. The first action step should be added as a root node (no parent).
    const parentStep = steps.find((s) => s.id === parentStepId);
    const isParentTriggerOrInput = parentStep && ['trigger', 'input'].includes(parentStep.type);
    const actualParentStepId = isParentTriggerOrInput ? null : parentStepId;

    graph = addStepToGraph({
      graph,
      stepId: data.id,
      parentStepId: actualParentStepId,
      relation,
      index
    });
  }

  return { step: payload, graph };
};
